import _isEmpty from 'underscore/cjs/isEmpty.js'
import dayjs from 'dayjs'

import { useUtmsStore } from './utms'
import BillUploadService from '~/services/energy-bill-upload.service'

const billUploadService = new BillUploadService()
 
export const useEnergyStore = defineStore('energy', {
  state: () => ({
    guides: {
      url: null,
      initialPage: ''
    },
    form: {},
    products: [],
    currentRetailers: []
  }),

  getters: {
    productRequest () {
      const form = this.form

      if (_isEmpty(form)) {
        return {}
      }

      const formData = flattenObj(form, 1)

      const payload = {
        concession: false,
        customerId: '',
        eBilling: true,
        electricityBillAvailable: false,
        electricityBillDays: 90,
        electricityControlledLoadUsage1: 0,
        electricityControlledLoadUsage2: 0,
        electricityCurrentSpend: 0,
        electricityCurrentSpendInclGst: 0,
        electricityCurrentSupplier: '',
        electricityCurrentSupplierId: 0,
        electricityDistributorId: '',
        electricityMeterType: 'Single',
        electricityOffPeakUsage: 0,
        electricityPeakUsage: 0,
        electricityReferenceUsage: true,
        electricityShoulderUsage: 0,
        electricitySolarFeed: 0,
        gasBillAvailable: false,
        gasBillDays: 90,
        gasBillType: 'Winter',
        gasCurrentSpend: 0,
        gasCurrentSpendInclGst: 0,
        gasCurrentSupplier: '',
        gasCurrentSupplierId: 0,
        gasDistributorId: '',
        gasHeating: false,
        gasMainsConnected: false,
        gasOffPeakUsage: 0,
        gasPeakUsage: 9000,
        hideProductDetails: false,
        includeBundleRequired: true,
        isConnection: false,
        lifeSupport: false,
        noContract: false,
        numberOfPeople: 3,
        payBillsOntime: false,
        referenceBillFrequency: 'Quarterly',
        renewableEnergy: false,
        solarPanels: false,
        swimmingPool: false,
        underFloorHeating: false
      }

      return {
        ...payload,
        postcode: formData?.address?.postcode || formData?.postcode,
        fuelType: formData?.fuel_type,
        accountType: formData?.account_type
      }
    },

    leadParams () {
      const form = this.form

      const { enableBillUpload, enableUsage, enableCurrentRetailer } = useRuntimeConfig().public

      if (_isEmpty(form)) {
        return {}
      }

      const utmsStore = useUtmsStore()
      const formData = flattenObj(form, 1)

      let address
      if (formData.account_type === 'SME') {
        address = [
          formData?.postcode?.suburb || undefined,
          formData?.postcode?.state || undefined,
          formData?.postcode?.postcode || undefined
        ].filter(item => item).join(', ').trim()
      } else {
        address = formData?.address?.addressline
      }
      const name = (() => {
        const _temp = getName(formData?.full_name)

        return {
          first_name: _temp.first_name,
          last_name: _temp.last_name,
          fullname: _temp.name
        }
      })()

      const obj = {
        ...name,
        email: formData?.email,
        phone: formData?.phone,

        fuel_type: formData?.fuel_type,
        is_moving: formData?.sales_type === 'Better Deal' ? 'no' : 'yes',
        moving_date: (formData?.sales_type === 'Better Deal' || !formData?.moving_date?.length) ? '' : dayjs(formData?.moving_date, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        sales_type: formData?.sales_type,
        account_type: formData?.account_type?.toLowerCase() === 'sme'
          ? 'Business'
          : 'Residential',
        own_or_rent: formData?.property_type,
        brand: 'CompareClub',

        address_unit_number: formData?.address?.unitNumber,
        address_street_number: formData?.address?.streetNumber,
        address_street_name: formData?.address?.streetName,
        address_suburb: formData?.postcode?.suburb || formData?.address?.suburb,
        state: formData?.postcode?.state || formData?.address?.state,
        postcode: formData?.postcode?.postcode || formData?.address?.postcode,

        business_name: formData?.business_name,

        conversion_name: formData?.conversion_name || '',

        form_page: import.meta.client && window.location.href,
        landing_page: utmsStore.GET_UTMS_LANDING_PAGE,
        referrer: import.meta.client && window.document.referrer,

        productRequest: this.productRequest,
        address
      }

      obj.msat_uid = formData?.msat_uid || ''
      if(formData?.fuel_type === 'Gas') {
        obj.msat_status = true
      } else {
        obj.msat_status = formData?.msat_status || false
      }

      if (enableBillUpload) {
        obj.bill_uid = formData?.bill_uid || ''
      }

      // HI-1430: widened from a bare toggle check to "toggle OR we actually
      // have an answer". The SQPP variant always asks these questions, so with
      // the toggles off the CRM would silently lose usage and retailer on every
      // variant lead — poisoning the RPE guardrail rather than failing loudly.
      //
      // Provably a no-op for control: control deletes these questions when the
      // toggles are off, so the seeded values stay '' and the branch is never
      // taken.
      if (enableUsage || formData?.energy_usage_estimate) {
        obj.size = formData?.energy_usage_estimate || ''
      }
      if (enableCurrentRetailer || formData?.electricity_retailer || formData?.gas_retailer) {
        obj.electricity_retailer = formData?.electricity_retailer || ''
        obj.gas_retailer = formData?.gas_retailer || ''
      }

      // Only include solar and life_support when partB (variantComparision) is enabled
      if (formData?.solar !== undefined && formData?.solar !== '') {
        obj.solar = formData?.solar === 'Yes' ? 'true' : 'false'
      }
      if (formData?.life_support !== undefined && formData?.life_support !== '') {
        obj.life_support = formData?.life_support
      }

      if (formData?.clubplus_enquiry !== undefined) {
        obj.clubplus_enquiry = formData?.clubplus_enquiry
      }
      if (formData?.clubplus_enquiry_origin) {
        obj.clubplus_enquiry_origin = formData?.clubplus_enquiry_origin
      }
      if (formData?.lead_source) {
        obj.lead_source = formData?.lead_source
      }
      if (formData?.interestedinhealth === true) {
        obj.needs_notes = 'interestedinhealth'
      }

      return obj
    }
  },

  actions: {
    async createLead (payload, fullResponse = false) {
      const utmsStore = useUtmsStore()

      const partnerRule = await this.getPartner({
        state: payload?.state,
        connection_type: (payload?.account_type.toLowerCase() === 'sme' || payload?.account_type.toLowerCase() === 'business')
          ? 'BUSINESS'
          : 'RESIDENTIAL'
      })
        .then(res => res?.data?.partner_rule ?? 'cc_eng_internal')
        .catch(error => {
          throw new Error(error)
        })

      const response = await useFetch('/api/lead/create', {
        method: 'post',
        body: {
          vertical: 'Energy',
          ...utmsStore.utms,
          ...payload,
          partner_rule: partnerRule
        }
      }).then(res => {
        const temp = res?.data.value

        if (temp?.error) {
          throw temp?.error
        }

        const data = temp?.data
        return fullResponse?data:(data?.lead_uuid ?? undefined)
      }).catch(error => {
        throw error
      })

      return response
    },

    async getProducts () {
      await useFetch('/api/energy/products', {
        method: 'post',
        body: {
          ...this.productRequest
        }
      }).then(res => {
        if (!res?.data?.error) {
          this.products = res?.data?.data
          setLocal('products', JSON.stringify(res?.data?.data))
        }
      }).catch(error => {
        throw new Error(error)
      })
    },

    async getPartner (payload) {
      const response = await useFetch('/api/lead/partner', {
        method: 'get',
        params: {
          vertical: 'Energy',
          ...payload
        }
      }).then(res => res?.data.value)

      return response
    },

    async getMsatAddress () {
      const form = this.form
    
      if (_isEmpty(form)) {
        return {}
      }
    
      const formData = flattenObj(form, 1)
      const address = {
        houseNumber: formData?.address?.apartment || '',
        streetNumber: formData?.address?.streetNumber || '',
        streetName: formData?.address?.streetName || '',
        streetType: formData?.address?.streetType || '',
        suburb: formData?.address?.suburb || '',
        stateCode: formData?.address?.state || '',
        postcode: formData?.address?.postcode || '',
            
        lotNumber: formData?.address?.lotNumber || '',
        unitType: formData?.address?.unitType || '',
        unitNumber: formData?.address?.unitNumber || '',
        msatStreetNumber: formData?.address?.msatStreetNumber || '',
        msatStreetNumberSuffix: formData?.address?.msatStreetNumberSuffix || '',
        msatStreetName: formData?.address?.msatStreetName || ''
      }
    
      const response = await $fetch('/api/energy/msat-address', {
        method: 'POST',
        body: {
          ...address
        }
      }).then(res => res?.data)
    
      return response
    },

    async confirmMsatAddress () {
      const form = this.form

      if (_isEmpty(form)) {
        return {}
      }

      const formData = flattenObj(form, 1)

      const nmiData = {
        nmi: formData?.selected_nmi || '',
        msat_uid: formData?.msat_uid || ''
      }

      const response = await $fetch('/api/energy/confirm-msat-address', {
        method: 'POST',
        body: {
          ...nmiData

        }
      }).then(res => res?.data)

      return response
    },

    async getBillUploadStatus (bill_uid = '') {
      const form = this.form

      if (_isEmpty(form) && !bill_uid) {
        return {}
      }

      const formData = flattenObj(form, 1)

      const billUid = {
        bill_uid: bill_uid || formData?.bill_uid || ''
      }

      const response = await $fetch('/api/energy/bill-upload-status', {
        method: 'POST',
        body: {
          ...billUid

        }
      }).then(res => res?.data)
        .catch(() => ({
          status: 'Error'
        }))

      return response
    },

    async uploadBill (bill_file = '') {
      const form = this.form

      if (_isEmpty(form) && !bill_file) {
        return {}
      }

      const formData = new FormData()
      const funnelFormData = flattenObj(form, 1)
      formData.append('bill_file', bill_file || funnelFormData?.upload_bill)

      const response = await billUploadService.new(formData)
      return response
    },

    async getCurrentRetailers () {
      this.currentRetailers = await $fetch('/api/energy/current-retailers', {
        method: 'GET'
      }).then(res => res?.data)
        .catch(error => {
          throw new Error(error)
        })

    },

    async getReferencePriceByPostcode (postcode, size = 'medium') {
      const response = await useFetch('/api/energy/reference-price-by-postcode', {
        method: 'POST',
        body: {
          postcode,
          size
        }
      }).then(res => res?.data)
        .catch(error => {
          throw new Error(error)
        })

      return response
    }
  }
})
